import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowRight, Heart, Lightbulb, Rocket, Target, Award, Users,
  Globe, Star, BookOpen, GraduationCap, Trophy, CheckCircle2, Sparkles
} from 'lucide-react';

const milestones = [
  {
    year: '2019',
    title: 'The Seed of Inspiration',
    content: 'Harish Ananthapadmanabhan founds APS Training Academy in Aurora, Illinois — a 501(c)(3) non-profit named to honor his mother, Leela Padmanabhan, and her lifelong dedication to community service and empowering others through education. The first free after-school coding classes launch under the name Ready Set Code at a local community center with just 12 students.',
    icon: Heart,
    color: 'bg-pink-500',
  },
  {
    year: '2020',
    title: 'Growing Through Challenge',
    content: 'When the pandemic forces schools to close, APS Training Academy pivots to virtual instruction — refusing to let a crisis derail children\'s access to STEM education. Online enrollment triples as families discover accessible, high-quality coding classes. The crisis reveals how deeply communities need reliable tech education.',
    icon: Globe,
    color: 'bg-blue-500',
  },
  {
    year: '2021',
    title: 'The Birth of Ready Set STEM',
    content: 'With demand growing beyond what spreadsheets and email could manage, the team evolves Ready Set Code into Ready Set STEM — a purpose-built platform combining STEM-accredited curriculum, gamification, progress tracking, and enrollment management. The rebrand reflects the expansion from coding-only classes into a full Science, Technology, Engineering, Math, and Robotics curriculum. The platform launches with courses in Scratch, Python, and web development.',
    icon: Rocket,
    color: 'bg-purple-500',
  },
  {
    year: '2022',
    title: 'School Partnerships Expand',
    content: 'Ready Set STEM partners with local school districts, park districts, and libraries across the Fox Valley region. After-school programs launch at 10+ schools. The robotics division is born with the first FIRST LEGO League teams competing at regional tournaments.',
    icon: BookOpen,
    color: 'bg-green-500',
  },
  {
    year: '2023',
    title: 'STEM.org Accreditation',
    content: 'After rigorous evaluation of program design, instructional quality, learner outcomes, and equity of access, STEM.org awards Ready Set STEM the coveted Accredited™ Trustmark — validating the curriculum as meeting the highest standards in STEM education.',
    icon: Award,
    color: 'bg-amber-500',
  },
  {
    year: '2024',
    title: 'Best In STEM™ — Top 5%',
    content: 'STEM.org recognizes Ready Set STEM with dual honors: Best In STEM™ and the Accredited™ Trustmark — a distinction reserved for the top 5% of programs worldwide. The curriculum now spans 10+ subjects including AI, cybersecurity, IoT, data science, CAD, and augmented reality.',
    icon: Trophy,
    color: 'bg-yellow-500',
  },
  {
    year: '2025',
    title: '12,000 Learners & Counting',
    content: 'Ready Set STEM and APS Training Academy have now served 12,000+ learners across 28 area schools, with 93% from families below the Area Median Income and 49% identifying as female or non-binary. Competitive robotics teams compete in FIRST and VEX tournaments across Illinois. The vision: make world-class STEM education accessible to every child.',
    icon: Sparkles,
    color: 'bg-rsc-blue',
  },
];

export default function OurStoryPage() {
  return (
    <div data-testid="our-story-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">
              Our Story
            </h1>
            <p className="text-xl text-white/90 mb-4">
              From 12 students in a community center to 12,000+ learners across 28 schools — 
              this is the story of how a mother's legacy of service became a movement to make 
              STEM education accessible to every child.
            </p>
            <p className="text-lg text-white/70 italic">
              "At the heart of every innovation lies a story. Ready Set STEM is no exception."
            </p>
          </div>
        </div>
      </section>

      {/* Founder Quote */}
      <section className="py-12 bg-white border-b">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-2xl md:text-3xl font-heading font-bold text-rsc-navy leading-relaxed">
              "Every child deserves the chance to explore technology and discover their potential — 
              regardless of their family's zip code or income."
            </blockquote>
            <p className="mt-4 text-gray-500">— Harish Ananthapadmanabhan, Founder</p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-gray-50">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Our Journey</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">From a single classroom to a STEM.org-recognized platform serving thousands.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, i) => {
              const Icon = milestone.icon;
              return (
                <div key={i} className="flex gap-6 md:gap-8 mb-12 last:mb-0">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-14 h-14 rounded-2xl ${milestone.color} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-3" />}
                  </div>
                  <div className="pb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-rsc-navy text-white text-sm font-bold rounded-full">{milestone.year}</span>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-rsc-navy">{milestone.title}</h3>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed">{milestone.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Numbers Behind the Story */}
      <section className="section-padding bg-rsc-navy text-white">
        <div className="container-app">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">The Numbers Behind the Story</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: '12,000+', label: 'Learners served since founding' },
              { value: '28', label: 'Area schools partnered' },
              { value: '93%', label: 'Students below Area Median Income' },
              { value: '49%', label: 'Female & non-binary learners' },
              { value: 'Top 5%', label: 'STEM.org Best In STEM™' },
              { value: '10+', label: 'STEM subjects taught' },
              { value: '6', label: 'Competitive robotics teams' },
              { value: '54%', label: 'Latino/Hispanic learners' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl md:text-4xl font-black text-rsc-yellow">{stat.value}</p>
                <p className="text-sm text-white/70 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-rsc-blue to-rsc-navy text-white">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Be Part of Our Story</h2>
          <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
            Join us in shaping the future of STEM education — as a student, parent, instructor, volunteer, or partner.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/catalog"><Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">Browse Courses <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/robotics"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Robotics Division</Button></Link>
            <Link to="/community-impact"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Community Impact</Button></Link>
            <Link to="/contact"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Contact Us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
